import React, { useRef, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";

// @YUp
import * as Yup from "yup";
import { Button, Grid, TextField, MenuItem, Select, FormControl, Box, Chip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Notification } from "../../utils/Notification";
import CustomSelect from "../../uicomponents/CustomSelect";
import TextArea from "../../uicomponents/TextArea";
import ReactCustomSelect from "../../uicomponents/ReactCustomSelect";
import { useGetJobCodes, useGetJobSkills, useUpdateCandidate } from "../../hooks/useCandidateManagement";
import useAuth from "../../hooks/useAuth";

const EditCandidate = () => {
  const { user: userDetails } = useAuth();
  const formikRef = React.useRef(null);

  const navigate = useNavigate();

  const fileInputRefs = {
    Resume: useRef(null),
  };

  const location = useLocation();
  const candidateDetails = location.state?.CandidateDetails;
  const [files, setFiles] = React.useState({
    Resume: candidateDetails ? candidateDetails.profile_path : null,
  });
  const [isFormSubmitted, setIsFormSubmitted] = React.useState(false);

  // Snackbar
  const [alert, setAlert] = React.useState(false);
  const [alertContent, setAlertContent] = React.useState("");
  const [alertType, setAlertType] = React.useState("");

  const handleAlert = () => {
    setAlert(false);
  };

  const handleFileChange = (event, docType) => {
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      // Allowed MIME types
      const allowedMimeTypes = [
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/pdf' // .pdf
      ];
      const allowedExtensions = ['.doc', '.docx', '.pdf'];

      // Check MIME type (primary validation)
      const isValidMime = allowedMimeTypes.includes(selectedFile.type);

      // Fallback: Check file extension
      const fileExtension = '.' + selectedFile.name.split('.').pop().toLowerCase();
      const isValidExtension = allowedExtensions.includes(fileExtension);

      if (isValidMime || isValidExtension) {
        // Optional: File size validation (max 5MB)
        if (selectedFile.size > 5 * 1024 * 1024) {
          setAlertContent("File too large. Please select a document under 5MB.");
          setAlertType("error");
          setAlert(true);
          event.target.value = ''; // Clear input
          return;
        }

        setFiles((prevFiles) => ({
          ...prevFiles,
          [docType]: selectedFile,
        }));
      } else {
        // Display error for invalid file type
        setAlertContent("Invalid file type. Please select a Word document (.doc or .docx) or a PDF (.pdf).");
        setAlertType("error");
        setAlert(true);
        event.target.value = ''; // Clear invalid file
      }
    }
  };

  const handleRemoveFile = (docType) => {
    setFiles((prevFiles) => ({
      ...prevFiles,
      [docType]: null,
    }));
  };

  const [selectedTechSkills, setSelectedTechSkills] = React.useState(
    Array.isArray(candidateDetails?.techSkills) ? candidateDetails.techSkills : []
  );

  const initialValues = {
    job_code: candidateDetails.job_code || null,
    candidate_name: candidateDetails.candidate_name,
    candidate_current_location: candidateDetails.candidate_current_location,
    mobile: candidateDetails.mobile,
    email: candidateDetails.email,
    currency: candidateDetails.currency,
    budget: candidateDetails.budget,
    budget_eom: candidateDetails.budget_eom,
    important_note: candidateDetails.important_note,
    coreSkillId: '', // Initialize empty, will be set by useEffect
    techSkills: [],
  };

  const { supplierId } = useAuth();

  // React Query Hooks
  const { data: jobCodeList } = useGetJobCodes(supplierId);
  const [selectedJobCode, setSelectedJobCode] = React.useState(candidateDetails.job_code || '');

  const { data: jobSkills } = useGetJobSkills(selectedJobCode);

  const { mutate: updateCandidateProfile } = useUpdateCandidate();

  const onSubmit = async (values) => {
    if (files.Resume == null) {
      setIsFormSubmitted(true);
    } else {
      const formData = new FormData();
      formData.append("job_code", values.job_code);
      formData.append("candidate_name", values.candidate_name);
      formData.append(
        "candidate_current_location",
        values.candidate_current_location
      );
      formData.append("mobile", values.mobile);
      formData.append("email", values.email);
      formData.append("currency", values.currency);
      formData.append("budget", values.budget);
      formData.append("budget_eom", values.budget_eom);
      formData.append("important_note", values.important_note);
      formData.append("coreSkillId", values?.coreSkillId);
      const techSkillsArray = Array.isArray(values.techSkills)
        ? values.techSkills
        : String(values.techSkills || '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
      formData.append("techSkills", JSON.stringify(techSkillsArray));
      formData.append('suppliercode', userDetails?.supplierId);
      formData.append("file", files.Resume);

      updateCandidateProfile({
        profileId: candidateDetails.profile_id,
        formData: formData
      }, {
        onSuccess: (res) => {
          setIsFormSubmitted(false);
          setAlertContent(res.message || "Candidate updated successfully");
          setAlertType("success");
          setAlert(true);
          setTimeout(() => {
            const from = location.state?.from || "";
            navigate("/candidate_data" + from);
          }, 1500);
        },
        onError: (error) => {
          setIsFormSubmitted(false);
          setAlertContent(error.response?.data?.error || error.message);
          setAlertType("error");
          setAlert(true);
        }
      });
    }
  };

  const emailRegExp =
    /^([a-zA-Z0-9])(([a-zA-Z0-9])*([\._-])?([a-zA-Z0-9]))*@(([a-zA-Z0-9\-])+(\.))+([a-zA-Z]{2,4})+$/i;

  const validationSchema = Yup.object().shape({
    job_code: Yup.string().required("This field is required."),
    candidate_name: Yup.string()
      .min(2, "Minimum 2 characters are required.")
      .max(50, "Maximum length is 50 characters.")
      .matches(
        /^[a-zA-Z]+(\s[a-zA-Z]+)*$/,
        "Invalid Candidate name format.Spaces at the start and end are not allowed."
      )
      .required("This field is required."),
    candidate_current_location: Yup.string()
      .required("This field is required.")
      .min(1, "Minimum 1 caharacter is required.")
      .max(50, "Maximum length is 200 characters.")
      .matches(/^[^\s].*$/, "Address should not start with a space."),
    mobile: Yup.string()
      .required("This field is required.")
      .matches(
        /^\+91\d{10}$/,
        "Please enter a valid 13-digit number starting with +91 and followed by 10 digits."
      ),
    email: Yup.string()
      .matches(emailRegExp, "Email must have @ symbol and domain name.")
      .required("This field is required."),
    currency: Yup.string().required("This field is required."),
    budget: Yup.string().required("This field is required."),
    budget_eom: Yup.string().required("This field is required."),
  });

  const navigateToPreviousPage = () => {
    const from = location.state?.from || "";
    if (from) {
      navigate("/candidate_data" + from);
    } else {
      navigate(-1);
    }
  };

  // Sync core skills
  React.useEffect(() => {
    if (jobSkills) {
      if (formikRef.current) {
        formikRef.current.setFieldValue("coreSkillId", jobSkills.coreSkills?.id || "");
      }
    }
  }, [jobSkills]);

  // Sync tech skills
  React.useEffect(() => {
    if (jobSkills?.techSkills && candidateDetails?.techSkills) {
      const jobTechSet = new Set(jobSkills.techSkills);

      const candidateSkillsArray = Array.isArray(candidateDetails.techSkills)
        ? candidateDetails.techSkills
        : typeof candidateDetails.techSkills === "string"
          ? candidateDetails.techSkills.split(",").map((s) => s.trim())
          : [];

      const validSkills = candidateSkillsArray.filter((skill) => jobTechSet.has(skill));

      setSelectedTechSkills(validSkills);
      if (formikRef.current) {
        formikRef.current.setFieldValue("techSkills", validSkills);
      }
    }
  }, [jobSkills, candidateDetails?.techSkills]);

  return (
    <div style={{ marginTop: 20 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={navigateToPreviousPage}>
        Back
      </Button>
      <div
        style={{
          border: "1px solid #989898",
          borderRadius: "10px",
          padding: "15px",
        }}
      >
        <div
          style={{
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontWeight: "500",
            letterSpacing: 1,
            textAlign: "left",
          }}
        >
          <span
            style={{
              fontSize: "20px",
            }}
          >
            Edit Profile
          </span>
          <div style={{ display: "block" }}>
            <Formik
              innerRef={formikRef}
              initialValues={initialValues}
              onSubmit={onSubmit}
              validationSchema={validationSchema}
            >
              {(props) => (
                <Form noValidate>
                  <Grid container spacing={2}>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Job Code</span>
                      <ReactCustomSelect
                        name="job_code"
                        props={props}
                        options={jobCodeList?.result?.map((job) => ({
                          value: job.job_code,
                          label: job.job_code,
                        })) || []}
                        disabled={candidateDetails ? true : false}
                        onChange={(selectedOption) => {
                          setSelectedJobCode(selectedOption?.value);
                          console.log("Selected option:", selectedOption);
                          // jobSkills will update automatically via Query
                          setSelectedTechSkills([]);
                          if (formikRef.current) {
                            formikRef.current.setFieldValue('coreSkillId', '');
                            formikRef.current.setFieldValue('techSkills', []);
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Core Skills</span>
                      <Field
                        name="coreskill"
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            // label="Core Skills"
                            value={jobSkills?.coreSkills?.name || ''}
                            InputProps={{
                              readOnly: true,
                              disabled: true  // Always disabled
                            }}
                            size="small"
                            error={props.errors.coreskill && props.touched.coreskill}
                            helperText={<ErrorMessage name="coreskill" />}
                          />
                        )}
                      />

                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: 16 }}>Technical Skills</span>
                      <FormControl fullWidth size="small">
                        <Select
                          multiple
                          value={selectedTechSkills}
                          onChange={(e) => {
                            const newTechSkills = e.target.value;
                            setSelectedTechSkills(newTechSkills);
                            if (formikRef.current) {
                              formikRef.current.setFieldValue('techSkills', newTechSkills);
                            }
                          }}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip key={value} label={value} size="small" />
                              ))}
                            </Box>
                          )}
                        >
                          {jobSkills?.techSkills?.map((skill) => (
                            <MenuItem
                              key={skill}
                              value={skill}
                              style={
                                Array.isArray(selectedTechSkills) && selectedTechSkills.includes(skill)
                                  ? { backgroundColor: '#e3f2fd', fontWeight: 500 }
                                  : {}
                              }
                            >
                              {skill}
                            </MenuItem>
                          ))}
                        </Select>

                      </FormControl>
                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Candidate Name</span>
                      <Field
                        as={TextField}
                        name="candidate_name"
                        fullWidth
                        error={
                          props.errors.candidate_name &&
                          props.touched.candidate_name
                        }
                        size="small"
                        helperText={<ErrorMessage name="candidate_name" />}
                        required
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Current Address</span>
                      <Field
                        as={TextField}
                        name="candidate_current_location"
                        fullWidth
                        error={
                          props.errors.candidate_current_location &&
                          props.touched.candidate_current_location
                        }
                        size="small"
                        helperText={
                          <ErrorMessage name="candidate_current_location" />
                        }
                        required
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Mobile</span>
                      <Field
                        as={TextField}
                        name="mobile"
                        fullWidth
                        error={props.errors.mobile && props.touched.mobile}
                        size="small"
                        helperText={<ErrorMessage name="mobile" />}
                        required
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Email</span>
                      <Field
                        as={TextField}
                        name="email"
                        fullWidth
                        error={props.errors.email && props.touched.email}
                        size="small"
                        helperText={<ErrorMessage name="email" />}
                        required
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Currency</span>
                      <CustomSelect
                        name="currency"
                        props={props}
                        options={allCurrency}
                      />
                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Budget</span>
                      <Field
                        as={TextField}
                        name="budget"
                        fullWidth
                        error={props.errors.budget && props.touched.budget}
                        size="small"
                        onInput={(e) =>
                        (e.target.value = e.target.value.replace(
                          /[^0-9.]/g,
                          ""
                        ))
                        }
                        helperText={<ErrorMessage name="budget" />}
                        required
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Budget EOM</span>
                      <CustomSelect
                        name="budget_eom"
                        props={props}
                        options={budgetUOM}
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}></Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Notes (Optional)</span>
                      <TextArea name="important_note" props={props} />
                    </Grid>

                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div
                        style={{
                          marginTop: 20,
                          border: "1px solid #989898",
                          borderRadius: "10px",
                          padding: "15px",
                        }}
                      >
                        <Grid container>
                          {Object.entries(files).map(
                            ([docType, file], index) => (
                              <Grid container spacing={2} key={index}>
                                {/* 1st Column */}
                                <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
                                  Upload Profile
                                </Grid>

                                {/* 2nd Column */}
                                <Grid
                                  item
                                  xl={6}
                                  lg={6}
                                  md={6}
                                  sm={6}
                                  xs={6}
                                  display="flex"
                                  alignItems="center"
                                >
                                  {file && (
                                    <>
                                      {file.name ? (
                                        <div>{file.name}</div>
                                      ) : (
                                        <div>{file}</div>
                                      )}
                                      <DeleteIcon
                                        style={{
                                          cursor: "pointer",
                                          color: "red",
                                          marginLeft: "5px",
                                        }}
                                        onClick={() =>
                                          handleRemoveFile(docType)
                                        }
                                      />
                                    </>
                                  )}
                                </Grid>

                                {/* 3rd Column */}
                                <Grid
                                  item
                                  xl={3}
                                  lg={3}
                                  md={3}
                                  sm={3}
                                  xs={3}
                                  display="flex"
                                  justifyContent="flex-end"
                                >
                                  <Button
                                    onClick={() =>
                                      fileInputRefs[docType].current.click()
                                    }
                                  >
                                    Browse
                                  </Button>
                                  <input
                                    type="file"
                                    hidden="true"
                                    accept=".doc,.docx,.pdf"
                                    onChange={(e) =>
                                      handleFileChange(e, docType)
                                    }
                                    ref={fileInputRefs[docType]}
                                  />
                                </Grid>
                              </Grid>
                            )
                          )}
                        </Grid>
                      </div>
                    </Grid>

                    {isFormSubmitted && files.Resume == null && (
                      <div
                        style={{
                          color: "#d32f2f",
                          marginLeft: "2rem",
                          fontWeight: 400,
                          fontSize: "0.75rem",
                        }}
                      >
                        This Field is required
                      </div>
                    )}

                    {/* Submit */}
                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Button
                          variant="contained"
                          aria-haspopup="true"
                          style={{
                            background: "#2777AA",
                            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                            fontSize: "16px",
                            fontWeight: "500",
                            letterSpacing: 1,
                          }}
                          type="submit"
                        >
                          Submit
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                  <Notification
                    message={alertContent}
                    open={alert}
                    severity={alertType}
                    onClose={handleAlert}
                  />
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCandidate;

const allCurrency = [
  { id: 1, value: "INR", label: "INR" },
  { id: 2, value: "USD", label: "USD" },
  { id: 3, value: "GBP", label: "GBP" },
];

const budgetUOM = [
  { id: 1, value: "perHour", label: "Per Hour" },
  { id: 2, value: "perMonth", label: "Per Month" },
  { id: 3, value: "perDay", label: "Per Day" },
];

const allStatus = [
  { id: 1, value: "Submitted", label: "Submitted" },
  { id: 2, value: "Reviewed", label: "Reviewed" },
  { id: 3, value: "Rejected", label: "Rejected" },
  { id: 4, value: "Short_listed", label: "Short Listed" },
  { id: 5, value: "Interview_Scheduled", label: "Interview Scheduled" },
  { id: 6, value: "Interview_Cancelled", label: "Interview Cancelled" },
];
